
import { useMemo } from 'react';

export interface TrackingEvent {
  id: string;
  status: string;
  title: string;
  description: string;
  location?: string;
  timestamp: string;
}

interface LiveTrackingTimelineProps {
  events: TrackingEvent[];
  currentStatus: string;
}

const statusConfig: Record<
  string,
  { icon: string; color: string; bg: string; border: string }
> = {
  order_placed: {
    icon: 'ri-checkbox-circle-line',
    color: 'text-[#2E7D32]',
    bg: 'bg-[#E8F5E9]',
    border: 'border-[#A5D6A7]',
  },
  payment_confirmed: {
    icon: 'ri-bank-card-line',
    color: 'text-[#2E7D32]',
    bg: 'bg-[#E8F5E9]',
    border: 'border-[#A5D6A7]',
  },
  processing: {
    icon: 'ri-settings-3-line',
    color: 'text-[#D4A574]',
    bg: 'bg-[#FFF8F0]',
    border: 'border-[#E8C9A0]',
  },
  quality_check: {
    icon: 'ri-shield-check-line',
    color: 'text-[#D4A574]',
    bg: 'bg-[#FFF8F0]',
    border: 'border-[#E8C9A0]',
  },
  packed: {
    icon: 'ri-gift-line',
    color: 'text-[#D4A574]',
    bg: 'bg-[#FFF8F0]',
    border: 'border-[#E8C9A0]',
  },
  shipped: {
    icon: 'ri-truck-line',
    color: 'text-[#1565C0]',
    bg: 'bg-[#E3F2FD]',
    border: 'border-[#90CAF9]',
  },
  in_transit: {
    icon: 'ri-route-line',
    color: 'text-[#1565C0]',
    bg: 'bg-[#E3F2FD]',
    border: 'border-[#90CAF9]',
  },
  out_for_delivery: {
    icon: 'ri-e-bike-2-line',
    color: 'text-[#E65100]',
    bg: 'bg-[#FFF3E0]',
    border: 'border-[#FFCC80]',
  },
  delivered: {
    icon: 'ri-home-smile-line',
    color: 'text-[#2E7D32]',
    bg: 'bg-[#E8F5E9]',
    border: 'border-[#A5D6A7]',
  },
  cancelled: {
    icon: 'ri-close-circle-line',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
};

const defaultConfig = {
  icon: 'ri-information-line',
  color: 'text-gray-500',
  bg: 'bg-gray-50',
  border: 'border-gray-200',
};

export default function LiveTrackingTimeline({
  events,
  currentStatus,
}: LiveTrackingTimelineProps) {
  const sortedEvents = useMemo(() => {
    return [...events].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }, [events]);

  const formatTimestamp = (ts: string) => {
    try {
      const d = new Date(ts);
      const date = d.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      const time = d.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return { date, time };
    } catch {
      return { date: 'Date inconnue', time: '' };
    }
  };

  const getTimeDiff = (ts: string) => {
    const now = new Date().getTime();
    const then = new Date(ts).getTime();
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `Il y a ${diffDays}j`;
  };

  if (sortedEvents.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
            <i className="ri-time-line text-lg text-[#2E7D32]"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 tracking-wider">
              SUIVI EN TEMPS RÉEL
            </h3>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Les mises à jour apparaîtront ici
            </p>
          </div>
        </div>
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
            <i className="ri-time-line text-2xl text-gray-300"></i>
          </div>
          <p className="text-sm text-gray-400">
            Aucun événement de suivi pour le moment
          </p>
          <p className="text-xs text-gray-300 mt-1">
            Les mises à jour apparaîtront dès que votre commande sera
            traitée
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
            <i className="ri-time-line text-lg text-[#2E7D32]"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 tracking-wider">
              SUIVI EN TEMPS RÉEL
            </h3>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {sortedEvents.length} mise
              {sortedEvents.length > 1 ? 's' : ''} à jour
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#E8F5E9] rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D32] animate-pulse"></span>
          <span className="text-[10px] font-semibold text-[#2E7D32] tracking-wider">
            LIVE
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {sortedEvents.map((event, idx) => {
          const config = statusConfig[event.status] || defaultConfig;
          const { date, time } = formatTimestamp(event.timestamp);
          const isFirst = idx === 0;
          const isLast = idx === sortedEvents.length - 1;

          return (
            <div
              key={event.id}
              className="relative flex gap-4 pb-6 last:pb-0 group"
            >
              {/* Vertical line */}
              {!isLast && (
                <div className="absolute left-[17px] top-[38px] bottom-0 w-[2px] bg-gradient-to-b from-gray-200 to-gray-100"></div>
              )}

              {/* Icon */}
              <div
                className={`relative z-10 w-[36px] h-[36px] rounded-full ${config.bg} border ${config.border} flex items-center justify-center flex-shrink-0 ${
                  isFirst
                    ? 'ring-4 ring-offset-1 ring-[#2E7D32]/10'
                    : ''
                } transition-all`}
              >
                <i className={`${config.icon} text-base ${config.color}`}></i>
              </div>

              {/* Content */}
              <div
                className={`flex-1 min-w-0 ${
                  isFirst
                    ? 'bg-[#FAFDF7] rounded-xl p-3.5 -mt-1 border border-[#E8F5E9]'
                    : 'pt-0.5'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold ${
                        isFirst ? 'text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      {event.title}
                    </p>
                    <p
                      className={`text-xs mt-0.5 leading-relaxed ${
                        isFirst ? 'text-gray-600' : 'text-gray-400'
                      }`}
                    >
                      {event.description}
                    </p>
                    {event.location && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <i className="ri-map-pin-2-line text-[11px] text-gray-400 w-3 h-3 flex items-center justify-center"></i>
                        <span className="text-[11px] text-gray-400">
                          {event.location}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] font-medium text-gray-500">
                      {date}
                    </p>
                    <p className="text-[10px] text-gray-400">{time}</p>
                    {isFirst && (
                      <span className="inline-block mt-1 text-[9px] font-semibold text-[#2E7D32] bg-[#E8F5E9] px-1.5 py-0.5 rounded">
                        {getTimeDiff(event.timestamp)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
